/**
 * fromCteRef — pure discriminator: does a plain FROM-item relation reference a
 * CTE declared in the enclosing WITH clause? (Task cte-context, wave 1,
 * COM-05/06/08.) Two signals, in priority order:
 *
 *  1. RESOLVER signal (preferred) — a resolved FROM item's
 *     `target.kind === 'cte-ref'` (see `compact/compactDegradeDetail.ts`'s
 *     `formatTargetName`, which reads the same union member). Available
 *     whenever `FromItemRow` has a live `QueryStructure`
 *     (`resolveFromStructure(editContext)` — edit-ready only). `FromItemRow`
 *     checks `target.kind` directly against the resolver's own
 *     `structure.fromItems[index]`; this module does not re-derive it.
 *
 *  2. NAME-MATCH fallback — when no edit-ready structure is reachable, parse
 *     the FROM item's leading identifier off its raw `leadingText` (ignoring
 *     any alias that follows) and case-fold-compare it against the CTE names
 *     declared by the enclosing WITH clause. The CTE-name set is built once by
 *     `SelectRenderer` from its already-extracted `CteOutline.bindings`
 *     (`extractCtes` — no re-parsing of the WITH clause) and threaded down
 *     through `FromSection` to `FromItemRow`. Folding mirrors the resolver's
 *     own `foldIdentifier` (bare vs quoted, per-engine default fold) so a
 *     bare `Order_Totals` FROM reference matches a declared `order_totals` CTE
 *     on an engine that folds unquoted identifiers to lower/upper case.
 */
import type { ParseOptions } from '../../../../sql-grammar/model';
import { foldIdentifier, readQualifiedName } from '../../../../sql-resolver/scope/identifiers';
import type { CteBinding } from './extractCtes';

export interface CteNameEntry {
  /** Declared spelling, exactly as written in the WITH clause (quotes stripped). */
  readonly name: string;
  /** Case-folded per engine identifier rules — the fallback-match comparison basis. */
  readonly foldedName: string;
}

// A CTE name is "quoted" when it opens with a double quote, backtick, or an
// MSSQL bracket — mirrors extractCtes.ts's private `isQuotedNameChar` (not
// exported there; re-derived here from the raw buffer char at `nameStart`
// rather than duplicating extractCtes's parse).
function isQuotedNameChar(c: string | undefined): boolean {
  return c === '"' || c === '`' || c === '[';
}

/**
 * Build the CTE-name lookup table `SelectRenderer` threads down through
 * `FromSection` → `FromItemRow`. Reuses the already-extracted
 * `CteOutline.bindings` (`extractCtes`) — no re-parsing of the WITH clause.
 */
export function buildCteNameEntries(
  bindings: readonly CteBinding[],
  bufferText: string,
  options: ParseOptions,
): readonly CteNameEntry[] {
  return bindings.map((b) => ({
    name: b.name,
    foldedName: foldIdentifier(b.name, isQuotedNameChar(bufferText[b.nameStart]), options),
  }));
}

/**
 * Fallback discriminator: parse the leading identifier out of a FROM item's
 * raw text (ignoring any alias that follows) and match it, case-folded,
 * against the threaded CTE-name set. Returns the matching entry (its declared
 * name is the scroll-to-definition target — the same spelling `CteCard` writes
 * into its `data-cte-name` anchor), or `null` when there is no match.
 */
export function matchCteRefByName(
  leadingText: string,
  cteNames: readonly CteNameEntry[],
  options: ParseOptions,
): CteNameEntry | null {
  if (cteNames.length === 0) return null;
  const parsed = readQualifiedName(leadingText, 0, leadingText.length, options);
  if (!parsed) return null;
  // A CTE reference is never schema-qualified — a dotted name rules it out.
  if (parsed.qualified.schema) return null;
  const folded = parsed.qualified.foldedName;
  return cteNames.find((entry) => entry.foldedName === folded) ?? null;
}
